import { claimBoardImage, Chatv1Error, mintUpload } from '../chatv1/transport'
import { postToS3 } from '../chat/projectDynamo'

/**
 * PICTURES ON A WHITEBOARD (owner, 2026-10-30: 讓 whiteboard 可以 import image). A picture travels the way a chat
 * attachment does — minted, POSTed straight to S3, then claimed — except that the board claims it, not a message
 * (chat lambda/boards.ts claimImage). The element that shows it is written after the claim, so everyone who sees the
 * element may already read the picture. It is shown through the app's one fetch-and-show lifecycle (ui/useBlobUrl,
 * in ElementView).
 *
 * A photo bigger than a board needs is made smaller first: a board shows a picture at a few hundred pixels, and a
 * 4000-pixel phone photo would cost every member's screen its full size for nothing.
 */

/** The longest side a picture keeps. */
const MAX_SIDE = 2560
/** The service's cap on one attachment. */
const MAX_BYTES = 25 * 1024 * 1024
/** Kinds every browser shows as they are; anything else the browser can open is made into a PNG or JPEG. */
const AS_IS = new Set(['image/png', 'image/jpeg', 'image/gif', 'image/webp'])

class ImageError extends Error {}

export type Prepared = { blob: Blob; type: string; name: string; w: number; h: number }

/** Is this a file a person would call a picture? (HEIC arrives with no type on some desktops.) */
export function isPicture(f: { type: string; name: string }): boolean {
  return f.type.startsWith('image/') || /\.(heic|heif|png|jpe?g|gif|webp|svg|bmp|avif)$/i.test(f.name)
}

/** An SVG's own size, from its width/height or its viewBox; a drawing with none still needs one on the board. */
async function svgSize(file: Blob): Promise<{ w: number; h: number }> {
  const doc = new DOMParser().parseFromString(await file.text(), 'image/svg+xml')
  const root = doc.documentElement
  if (root.nodeName !== 'svg') throw new ImageError('unreadable')
  const num = (v: string | null) => (v && /^\s*[\d.]+(px)?\s*$/.test(v) ? parseFloat(v) : NaN)
  let w = num(root.getAttribute('width'))
  let h = num(root.getAttribute('height'))
  const vb = (root.getAttribute('viewBox') ?? '').trim().split(/[\s,]+/).map(Number)
  if (!(w > 0 && h > 0) && vb.length === 4 && vb[2]! > 0 && vb[3]! > 0) [w, h] = [vb[2]!, vb[3]!]
  return w > 0 && h > 0 ? { w, h } : { w: 800, h: 600 }
}

function encode(canvas: HTMLCanvasElement, type: string): Promise<Blob> {
  return new Promise((resolve, reject) =>
    canvas.toBlob((b) => (b ? resolve(b) : reject(new ImageError('unreadable'))), type, 0.88),
  )
}

/** The picture as it will be uploaded: its own bytes when they will do, else smaller or in a kind every screen shows. */
export async function prepareImage(file: File): Promise<Prepared> {
  if (file.type === 'image/svg+xml' || /\.svg$/i.test(file.name)) {
    if (file.size > MAX_BYTES) throw new ImageError('too_big')
    return { blob: file, type: 'image/svg+xml', name: file.name, ...(await svgSize(file)) }
  }
  let bmp: ImageBitmap
  try {
    bmp = await createImageBitmap(file)
  } catch {
    throw new ImageError('unreadable')
  }
  try {
    const w = bmp.width
    const h = bmp.height
    if (!w || !h) throw new ImageError('unreadable')
    if (Math.max(w, h) <= MAX_SIDE && AS_IS.has(file.type) && file.size <= MAX_BYTES) {
      return { blob: file, type: file.type, name: file.name, w, h }
    }
    const k = Math.min(1, MAX_SIDE / Math.max(w, h))
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(w * k))
    canvas.height = Math.max(1, Math.round(h * k))
    const g = canvas.getContext('2d')
    if (!g) throw new ImageError('unreadable')
    g.drawImage(bmp, 0, 0, canvas.width, canvas.height)
    // Kinds that may be see-through stay see-through; photos become JPEG.
    const type = file.type === 'image/png' || file.type === 'image/gif' || file.type === 'image/webp' ? 'image/png' : 'image/jpeg'
    const blob = await encode(canvas, type)
    if (blob.size > MAX_BYTES) throw new ImageError('too_big')
    const name = file.name.replace(/\.[^.]*$/, '') + (type === 'image/png' ? '.png' : '.jpg')
    return { blob, type, name, w: canvas.width, h: canvas.height }
  } finally {
    bmp.close()
  }
}

const wait = (ms: number) => new Promise((r) => setTimeout(r, ms))

/** Upload a prepared picture and claim it for the board. Returns its attachment key — the element's `src`. */
export async function uploadImage(conv: string, board: string, p: Prepared): Promise<string> {
  const mint = await mintUpload(conv, p.blob.size, p.type)
  const up = await postToS3(mint, p.blob, p.name)
  if (!up.ok) throw new ImageError('upload')
  // S3 tells the service the file arrived a moment after it did; until then the claim says `not_ready`.
  for (let i = 0; ; i++) {
    try {
      await claimBoardImage(conv, board, mint.s3_key)
      return mint.s3_key
    } catch (e) {
      if (!(e instanceof Chatv1Error && e.code === 'not_ready' && i < 15)) throw e
      await wait(400 + i * 200)
    }
  }
}

/** What a failed import says to the person. */
export function importText(e: unknown): string {
  if (e instanceof ImageError) {
    if (e.message === 'unreadable') return 'That file could not be opened as a picture — try a PNG or JPEG.'
    if (e.message === 'too_big') return 'That picture is larger than 25 MB.'
    return 'The picture did not upload — check the connection and try again.'
  }
  if (e instanceof DOMException && e.name === 'TimeoutError') return 'The picture upload stalled — try again.'
  if (e instanceof Chatv1Error && e.code === 'not_a_member') return 'You are no longer in this project.'
  return 'The picture could not be added — try again.'
}
